import React, { useState, useRef, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Box, Typography, Avatar, TextField, Button, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Chip, Drawer, Fab } from '@mui/material'
import { Person as PersonIcon, AutoAwesome as AutoAwesomeIcon, Send as SendIcon, Info as InfoIcon, SmartToy as SmartToyIcon, ChevronRight as ChevronRightIcon } from '@mui/icons-material'
import { useTheme } from '@mui/material/styles'
import { checkEmpty, imageAddress } from 'data/helpers'
import { fetchAIJobs, fetchAIJobsFuzzy, addChatMessage, setAiTyping } from '/src/redux/aiJobSearch'
import TypingIndicator from './TypingIndicator'

const AIChatBot = () => {
  const theme = useTheme()
  const dispatch = useDispatch()
  const [prompt, setPrompt] = useState('')
  const [sourcesModalOpen, setSourcesModalOpen] = useState(false)
  const [selectedSources, setSelectedSources] = useState('')
  const [drawerOpen, setDrawerOpen] = useState(true)
  const messagesEndRef = useRef(null)

  // Get user from state
  const candidate = useSelector(state => state.candidate)
  const profile = useSelector(state => state.profile?.profile)
  const { exactMatches, fuzzyMatches, loading: aiLoading, chatHistory, isAiTyping } = useSelector(state => state.aiJobSearch)

  const profileImageObj = profile?.profileImage || candidate?.profileImage
  const profileImage = profileImageObj && !checkEmpty(profileImageObj.key) 
    ? imageAddress(profileImageObj.key, profileImageObj.fileName)
    : null

  useEffect(() => {
    if (exactMatches?.totalCount !== undefined) {
      // Only add the message if there are user messages in the chat history
      const hasUserMessages = chatHistory.some(msg => msg.type === 'user')
      if (hasUserMessages) {
        dispatch(addChatMessage({
          type: 'ai',
          message: `We have found ${exactMatches.totalCount} jobs that match your preferences.`,
          metadata: { 
            isReason: true, 
            jobsCount: exactMatches.totalCount,
            reasonForSelection: exactMatches.reasonForSelection
          }
        }))
        dispatch(setAiTyping(false))
      }
    }
  }, [exactMatches, dispatch])

  // Add welcome message on first load
  useEffect(() => {
    if (chatHistory.length === 0) {
      dispatch(addChatMessage({
        type: 'ai',
        message: "👋 Hello! I'm your AI Assistant, ready to help you find the perfect job opportunities. You can ask me things like:\n\n• 'Find emergency medicine jobs in California'\n• 'Show me part-time positions with good benefits'\n• 'What cardiology jobs are available in Boston?'\n\nWhat kind of position are you looking for today?",
        metadata: { isWelcome: true }
      }))
    }
  }, [dispatch, chatHistory.length])

  // Scroll to bottom when new messages are added
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [chatHistory, isAiTyping])

  // Handle AI search failures
  useEffect(() => {
    // If exactMatches is empty and we're not loading, it might be an error
    if (!aiLoading && exactMatches?.totalCount === undefined && !isAiTyping && chatHistory.length > 0) {
      const lastMessage = chatHistory[chatHistory.length - 1]
      if (lastMessage?.type === 'user') {
        // Add error message only if the last message was from user and we haven't responded
        const hasAIResponse = chatHistory.some(msg => msg.type === 'ai' && msg.timestamp > lastMessage.timestamp)
        if (!hasAIResponse) {
          dispatch(addChatMessage({
            type: 'ai',
            message: 'I apologize, but I encountered an error processing your request. Please try again.',
            metadata: { isError: true }
          }))
          dispatch(setAiTyping(false))
        }
      }
    }
  }, [aiLoading, exactMatches, isAiTyping, chatHistory, dispatch])

  const handleSourcesClick = (reasonForSelection) => {
    setSelectedSources(reasonForSelection)
    setSourcesModalOpen(true)
  }

  const handleCloseModal = () => {
    setSourcesModalOpen(false)
    setSelectedSources('')
  }

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen)
  }

  // Add/remove class to body to handle main content margin
  useEffect(() => {
    // Inject global CSS for drawer state
    const styleId = 'ai-drawer-styles'
    let styleElement = document.getElementById(styleId)
    
    if (!styleElement) {
      styleElement = document.createElement('style')
      styleElement.id = styleId
      document.head.appendChild(styleElement)
    }

    if (drawerOpen) {
      document.body.classList.add('ai-drawer-open')
      document.documentElement.classList.add('ai-drawer-open')
      
      // Inject CSS for drawer open state
      styleElement.textContent = `
        /* Push the entire main content area to make room for drawer */
        body.ai-drawer-open main,
        body.ai-drawer-open #root,
        body.ai-drawer-open [data-testid="main-content"] {
          margin-right: 450px !important;
          transition: margin-right 0.3s ease !important;
        }
        
        /* Alternative targeting for the page wrapper */
        body.ai-drawer-open .MuiContainer-root:not(.ai-drawer-container) {
          margin-right: 450px !important;
          transition: margin-right 0.3s ease !important;
          max-width: none !important;
        }
        
        @media (max-width: 959px) {
          body.ai-drawer-open main,
          body.ai-drawer-open #root,
          body.ai-drawer-open [data-testid="main-content"],
          body.ai-drawer-open .MuiContainer-root:not(.ai-drawer-container) {
            margin-right: 0 !important;
          }
        }
      `
      
      console.log('AI Drawer opened - global styles injected')
    } else {
      document.body.classList.remove('ai-drawer-open')
      document.documentElement.classList.remove('ai-drawer-open')
      
      // Clear the styles
      styleElement.textContent = ''
      
      console.log('AI Drawer closed - global styles cleared')
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove('ai-drawer-open')
      document.documentElement.classList.remove('ai-drawer-open')
      const style = document.getElementById(styleId)
      if (style) {
        style.remove()
      }
    }
  }, [drawerOpen])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!prompt.trim()) return

    // Add user message to Redux chat history
    dispatch(addChatMessage({
      type: 'user',
      message: prompt
    }))

    // Set AI typing indicator
    dispatch(setAiTyping(true))
    
    // Clear input box
    setPrompt('')
    
    dispatch(fetchAIJobs({ values: { prompt, previousJobs: exactMatches?.jobs?.slice(0, 10) || [] } }))
    dispatch(fetchAIJobsFuzzy({ values: { prompt, previousJobs: fuzzyMatches?.jobs?.slice(0, 10) || [] } }))
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit(e)
    }
  }

  return (
    <>
      {/* AI Assistant Toggle Button */}
      <Box
        onClick={toggleDrawer}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: drawerOpen ? 474 : 24,
          zIndex: 9999,
          transition: 'right 0.3s ease',
          cursor: 'pointer',
        }}
      >
        {drawerOpen ? (
          <Fab
            color="primary"
            aria-label="collapse AI assistant"
            sx={{
              background: `linear-gradient(45deg, ${theme.palette.secondary.main} 30%, ${theme.palette.info.main} 90%)`,
              '&:hover': {
                background: `linear-gradient(45deg, ${theme.palette.secondary.dark} 30%, ${theme.palette.info.dark} 90%)`,
              },
              boxShadow: theme.palette.shadows.colored.secondary,
            }}
          >
            <ChevronRightIcon sx={{ color: theme.palette.common.white, fontSize: '1.5rem' }} />
          </Fab>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
            }}
          >
            {/* Pulsing indicator */}
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                background: `linear-gradient(45deg, ${theme.palette.secondary.main} 30%, ${theme.palette.info.main} 90%)`,
                animation: 'aiPulse 2s ease-in-out infinite',
                '@keyframes aiPulse': {
                  '0%': { opacity: 0.6, transform: 'scale(1)' },
                  '50%': { opacity: 1, transform: 'scale(1.3)' },
                  '100%': { opacity: 0.6, transform: 'scale(1)' },
                },
              }}
            />
            
            {/* Main FAB with label */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                background: `linear-gradient(45deg, ${theme.palette.secondary.main} 30%, ${theme.palette.info.main} 90%)`,
                borderRadius: '24px',
                padding: '12px 16px',
                boxShadow: theme.palette.shadows.colored.secondary,
                '&:hover': {
                  background: `linear-gradient(45deg, ${theme.palette.secondary.dark} 30%, ${theme.palette.info.dark} 90%)`,
                  transform: 'translateY(-2px)',
                  boxShadow: theme.palette.shadows.heavy,
                },
                transition: 'all 0.3s ease',
              }}
            >
              <SmartToyIcon sx={{ color: theme.palette.common.white, fontSize: '1.5rem', mr: 1 }} />
              <Typography
                variant="button"
                sx={{
                  color: theme.palette.common.white,
                  fontWeight: 'bold',
                  fontSize: '0.9rem',
                  textTransform: 'none',
                }}
              >
                AI Assistant
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      {/* AI Assistant Drawer */}
      <Drawer
        anchor="right"
        variant="persistent"
        open={drawerOpen}
        className="ai-drawer-container"
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 400, md: 450 },
            maxWidth: '100vw',
            height: '100vh',
            zIndex: 1200, // Lower than default drawer z-index
          }
        }}
      >
        <Box
          sx={{
            height: '100vh',
            backgroundColor: theme.palette.background.default,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              borderBottom: 1,
              borderColor: theme.palette.grey[300],
              background: `linear-gradient(45deg, ${theme.palette.secondary.main} 30%, ${theme.palette.info.main} 90%)`,
              color: theme.palette.common.white,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
              <SmartToyIcon />
              AI Assistant
            </Typography>
            <Button
              onClick={toggleDrawer}
              startIcon={<ChevronRightIcon />}
              sx={{ 
                color: theme.palette.common.white,
                textTransform: 'none',
                fontWeight: 'bold',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                }
              }}
            >
              Collapse
            </Button>
          </Box>
        {/* Messages Area */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {chatHistory.map((message) => (
            <Box
              key={message.id}
              sx={{
                display: 'flex',
                gap: 2,
                alignItems: 'flex-start',
                flexDirection: message.type === 'user' ? 'row-reverse' : 'row',
              }}
            >
              {message.type === 'user' ? (
                <Avatar
                  src={profileImage}
                  sx={{
                    bgcolor: theme.palette.primary.main,
                  }}
                >
                  <PersonIcon />
                </Avatar>
              ) : (
                <Avatar
                  sx={{
                    background: `linear-gradient(45deg, ${theme.palette.secondary.main} 30%, ${theme.palette.info.main} 90%)`,
                    '&:hover': {
                      transform: 'scale(1.05)',
                      transition: 'transform 0.2s ease-in-out',
                    },
                  }}
                >
                  <AutoAwesomeIcon
                    sx={{
                      color: theme.palette.common.white,
                      fontSize: '1.5rem',
                      animation: 'sparkle 1.5s infinite alternate',
                      '@keyframes sparkle': {
                        '0%': { opacity: 0.8, transform: 'scale(1)' },
                        '100%': { opacity: 1, transform: 'scale(1.2)' },
                      },
                    }}
                  />
                </Avatar>
              )}
              <Box
                sx={{
                  maxWidth: '80%',
                  backgroundColor: message.isError 
                    ? theme.palette.error.light 
                    : (message.type === 'user' ? theme.palette.primary.light : theme.palette.grey[100]),
                  borderRadius: 2,
                  p: 2,
                  border: message.isError ? `1px solid ${theme.palette.error.main}` : 'none',
                }}
              >
                <Typography 
                  variant='body1' 
                  sx={{ 
                    whiteSpace: 'pre-wrap',
                    color: message.isError ? theme.palette.error.dark : 'inherit'
                  }}
                >
                  {message.message}
                </Typography>
                {message.jobsCount && (
                  <Typography variant='body2' sx={{ color: theme.palette.text.secondary, mt: 1 }}>
                    Found {message.jobsCount} job{message.jobsCount !== 1 ? 's' : ''}
                  </Typography>
                )}
                {message.reasonForSelection && (
                  <Box sx={{ mt: 1 }}>
                    <Chip
                      icon={<InfoIcon />}
                      label="Sources"
                      size="small"
                      variant="outlined"
                      clickable
                      onClick={() => handleSourcesClick(message.reasonForSelection)}
                      sx={{
                        borderColor: theme.palette.primary.main,
                        color: theme.palette.primary.main,
                        '&:hover': {
                          backgroundColor: theme.palette.primary.light,
                          color: theme.palette.primary.dark,
                        },
                      }}
                    />
                  </Box>
                )}
              </Box>
            </Box>
          ))}
          {isAiTyping && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                mb: 2,
              }}
            >
              <TypingIndicator />
            </Box>
          )}
          <div ref={messagesEndRef} />
        </Box>

        {/* Input Area */}
        <Box
          component='form'
          onSubmit={handleSubmit}
          sx={{
            p: 2,
            borderTop: 1,
            borderColor: theme.palette.grey[300],
            backgroundColor: theme.palette.background.paper,
            borderBottomLeftRadius: 16,
            borderBottomRightRadius: 16,
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '2px',
              background: isAiTyping 
                ? `linear-gradient(90deg, ${theme.palette.secondary.main}, ${theme.palette.info.main}, ${theme.palette.secondary.main})`
                : 'transparent',
              backgroundSize: '200% 100%',
              animation: isAiTyping ? 'searchProgress 2s linear infinite' : 'none',
              '@keyframes searchProgress': {
                '0%': { backgroundPosition: '200% 0' },
                '100%': { backgroundPosition: '-200% 0' },
              },
            },
          }}
        >
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
            <TextField
              fullWidth
              multiline
              maxRows={4}
              variant='outlined'
              placeholder="Ask your AI Assistant about jobs! Try: 'Find emergency medicine jobs in California' or 'Show me part-time cardiology positions with good benefits'"
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isAiTyping}
              sx={{
                fontFamily: '"Kumbh Sans", sans-serif',
                '& .MuiOutlinedInput-root': {
                  fontFamily: '"Kumbh Sans", sans-serif',
                  '& input': {
                    fontFamily: '"Kumbh Sans", sans-serif',
                  },
                  '& textarea': {
                    fontFamily: '"Kumbh Sans", sans-serif',
                  },
                  '& fieldset': {
                    borderColor: isAiTyping ? theme.palette.secondary.main : 'rgba(0, 0, 0, 0.23)',
                    borderWidth: isAiTyping ? '2px' : '1px',
                    transition: 'border-color 0.3s ease, border-width 0.3s ease',
                  },
                  '&:hover fieldset': {
                    borderColor: theme.palette.secondary.main,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: theme.palette.secondary.main,
                  },
                  ...(isAiTyping && {
                    '& fieldset': {
                      borderColor: theme.palette.secondary.main,
                      borderWidth: '2px',
                      boxShadow: `0 0 0 1px ${theme.palette.secondary.main}33`,
                      animation: 'searchGlow 2s ease-in-out infinite',
                      '@keyframes searchGlow': {
                        '0%': { boxShadow: `0 0 0 1px ${theme.palette.secondary.main}33` },
                        '50%': { boxShadow: `0 0 0 3px ${theme.palette.secondary.main}66` },
                        '100%': { boxShadow: `0 0 0 1px ${theme.palette.secondary.main}33` },
                      },
                    },
                  }),
                },
              }}
            />
            <Button
              type='submit'
              variant='contained'
              disabled={isAiTyping || !prompt.trim()}
              sx={{
                minWidth: '56px',
                height: '56px',
                alignSelf: 'flex-end',
                background: `linear-gradient(45deg, ${theme.palette.secondary.main} 30%, ${theme.palette.info.main} 90%)`,
                '&:hover': {
                  background: `linear-gradient(45deg, ${theme.palette.secondary.dark} 30%, ${theme.palette.info.dark} 90%)`,
                },
              }}
            >
              {isAiTyping ? <CircularProgress size={24} color='inherit' /> : <SendIcon />}
            </Button>
          </Box>
        </Box>

        {/* Sources Modal */}
        <Dialog
          open={sourcesModalOpen}
          onClose={handleCloseModal}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
            },
          }}
        >
          <DialogTitle sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
            Sources & Reasoning
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
              {selectedSources}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button 
              onClick={handleCloseModal} 
              variant="contained"
              sx={{
                background: `linear-gradient(45deg, ${theme.palette.secondary.main} 30%, ${theme.palette.info.main} 90%)`,
                '&:hover': {
                  background: `linear-gradient(45deg, ${theme.palette.secondary.dark} 30%, ${theme.palette.info.dark} 90%)`,
                },
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
        </Box>
      </Drawer>
    </>
  )
}

export default AIChatBot
